import { memo, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { formatCurrency } from '@atc/string-fns';

import { RadioWithVisual, Text } from 'reaxl';

import { placeholderImages } from 'axl-config';

import { kbbVRSDataDuck } from '@/ducks';

import { bapActiveModelDuck, bapModelInfoDuck, bapSelectedOptionsDuck } from '@/ducks/bap';

function BuildAndPriceBodyStyleModel({
    activeStyleTab,
    makeCode,
    activeModel,
    initialModelStyle,
    interactedChangeModelLink,
}) {
    const vrsData = useSelector((state) => kbbVRSDataDuck.selectors.getData(state));
    const vrsModels = Object.keys(_get(vrsData, `${makeCode}`, {}));
    const dispatch = useDispatch();

    // change model when clicked in tab & fetch trim data for that model
    const handleModelChange = useCallback(
        (selectedModel) => {
            if (selectedModel !== activeModel) {
                dispatch(bapSelectedOptionsDuck.creators.reset());
                dispatch(
                    kbbVRSDataDuck.creators.addModelInfo({
                        makeCode,
                        modelCode: selectedModel,
                        selectedModel,
                    })
                );
                dispatch(bapActiveModelDuck.creators.setActiveModel(selectedModel));

                const selectedTrim = _get(vrsData, `${makeCode}.${selectedModel}`, {});
                if (!_isEmpty(selectedTrim)) {
                    dispatch(bapModelInfoDuck.creators.setActiveTrim(Object.keys(selectedTrim)?.[0]));
                }
            }
        },
        [activeModel, dispatch, makeCode, vrsData]
    );

    const renderRadioVisual = useCallback((firstTrim, modelOptionLabel) => (
        <RadioWithVisual
            key={firstTrim?.atcModelCode}
            onChange={() => handleModelChange(firstTrim?.atcModelCode)}
            uiContext="landscape"
            src={firstTrim?.imagePath || placeholderImages.noImageAvailable}
            imageClassName={clsx('margin-right-4', {
                'padding-horizontal-2': !(firstTrim?.imagePath),
            })}
            checked={activeModel === firstTrim?.atcModelCode}
            option={{
                label: modelOptionLabel,
                value: firstTrim?.atcModelCode,
            }}
        />
    ), [handleModelChange, activeModel]);

    return (
        <>
            {vrsModels.map((modelValue) => {
                const allTrims = Object.values(_get(vrsData, `${makeCode}.${modelValue}`, []));
                const trimsWithActiveStyle = allTrims.filter((trim) => trim?.bodyStyle === activeStyleTab);
                const firstTrim = trimsWithActiveStyle?.[0];

                if (!firstTrim) {
                    return null;
                }

                // order of priority for fuel economy: 1) EV Range, 2) mpgE, 3) mpg
                const rangeLabel = firstTrim?.estimatedElectricRange ? 'est. epa' : '';
                const mpgELabel = !firstTrim?.estimatedElectricRange && firstTrim?.mpgECombined?.low ? 'est. mpge' : rangeLabel;
                const mpgLabel = !firstTrim?.mpgECombined?.low && firstTrim?.mpgCombined?.low ? 'est. mpg' : mpgELabel;

                const mpgLow = firstTrim?.mpgECombined?.low || firstTrim?.mpgCombined?.low;
                const mpgHigh = firstTrim?.mpgECombined?.high || firstTrim?.mpgCombined?.high;
                const mpg = (mpgLow && mpgHigh && mpgLow !== mpgHigh) ? `${mpgLow} / ${mpgHigh}` : mpgLow;

                const range = firstTrim?.estimatedElectricRange ? firstTrim?.estimatedElectricRange : mpg;

                const price = formatCurrency(firstTrim?.ncbbPrice || firstTrim?.nationalMSRP);

                const modelOptionLabel = (
                    <>
                        <div>{firstTrim?.atcModel}</div>
                        <div className="display-flex">
                            <div
                                className={clsx('padding-right-2', { 'border-gray-light border-left-0 border-vertical-0': range && mpgLabel })}
                            >
                                <Text
                                    weight="bold"
                                    size={500}
                                    componentClass="div"
                                >
                                    {price}
                                </Text>
                                <Text
                                    color="subdued-lighter"
                                    size={200}
                                >
                                    starting mrsp
                                </Text>
                            </div>
                            <div className="padding-left-4">
                                <Text size={500}>{range}</Text>
                                <Text
                                    color="subdued-lighter"
                                    size={200}
                                >
                                    {mpgLabel}
                                </Text>
                            </div>
                        </div>
                    </>
                );

                // show all models under style tab if not on the initial tab or user has clicked change models under the
                // initial tab
                const shouldShowModel = (activeStyleTab !== initialModelStyle)
                || (interactedChangeModelLink && activeStyleTab === initialModelStyle)
                || activeModel === firstTrim?.atcModelCode;

                return shouldShowModel && renderRadioVisual(firstTrim, modelOptionLabel);
            })}
        </>
    );
}

export default memo(BuildAndPriceBodyStyleModel);
